"use client"

import { useState } from "react"
import Link from "next/link"
import {
  SectionRule,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { GuideTool } from "@/components/tools/guide-tool"

// ---------- Data ----------

interface Side {
  key: "french" | "side"
  name: string
  tag: string
  icon: string
  color: string
  pros: string[]
  cons: string[]
  score: Record<string, number>
}

const SIDE_A: Side = {
  key: "french",
  name: "French door",
  tag: "The flexible pick",
  icon: "⟵|⟶",
  color: "#0469f0",
  pros: [
    "Wider shelf space — no split",
    "Bottom freezer saves back",
    "Higher resale value",
    "Better for platters & pizza boxes",
  ],
  cons: [
    "Needs 36\" door swing clearance",
    "Typically $$$ more",
    "More repair-prone with ice/water dispensers",
  ],
  score: { capacity: 95, access: 88, efficiency: 82, value: 72, narrow: 60 },
}

const SIDE_B: Side = {
  key: "side",
  name: "Side-by-side",
  tag: "The narrow-kitchen pick",
  icon: "⟷",
  color: "#ff9951",
  pros: [
    "Fits narrow galley kitchens",
    "Eye-level freezer access",
    "Cheaper upfront",
    "Easier to organize tall items",
  ],
  cons: [
    "Narrow shelves — nothing wide fits",
    "Freezer/fridge both reduced width",
    "Higher energy use",
  ],
  score: { capacity: 70, access: 75, efficiency: 68, value: 85, narrow: 92 },
}

interface Scenario {
  who: string
  winner: "french" | "side"
  reason: string
}

const SCENARIOS: Scenario[] = [
  { who: "Family of 4 with kids", winner: "french", reason: "Wider shelves hold school lunches, casserole dishes, and the weekly Costco haul." },
  { who: "Single or couple in a galley kitchen", winner: "side", reason: "Side-by-sides fit behind a 32\"–36\" doorway that French doors can't clear." },
  { who: "Entertainer with big platters", winner: "french", reason: "37\" interior width fits a full turkey pan on one shelf." },
  { who: "Budget under $1,800", winner: "side", reason: "Side-by-sides start at $999; French doors rarely dip below $1,499." },
  { who: "Anyone with mobility concerns", winner: "french", reason: "Freezer drawer at the bottom is easier than reaching overhead." },
  { who: "Short on counter depth", winner: "side", reason: "Side-by-side counter-depth units run 1–2\" shallower on average." },
]

// ---------- Page ----------

export function FrenchDoorVsSideBySidePageClient() {
  const [tab, setTab] = useState<"french" | "side">("french")

  return (
    <ToolPageShell
      toolName="French Door vs Side-by-Side Refrigerator"
      heroEyebrow="Door styles"
      heroTitlePre="French door"
      heroTitleItalic="vs"
      heroTitlePost="side-by-side."
      heroSubtitle="The fridge door layout you pick shapes what groceries fit, what fits in your kitchen, and what you pay for a decade. Here's the honest breakdown."
      heroMeta={["⏱ ~3 min read"]}
      heroShare={<ToolShareBar title="French Door vs Side-by-Side Refrigerator" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-50 border border-gray-200">
          <div className="flex items-start gap-3">
            <div
              className="font-serif text-[#ff9951] leading-none flex-shrink-0"
              style={{ fontSize: 40, letterSpacing: "-0.05em" }}
            >
              &ldquo;
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] font-bold mb-2 text-[#ff9951]">
                The short answer
              </div>
              <p
                className="font-medium text-gray-900"
                style={{
                  fontSize: 16,
                  lineHeight: 1.5,
                  letterSpacing: "-0.01em",
                  textWrap: "pretty" as never,
                }}
              >
                Pick <em className="italic text-[#ff9951] font-bold">French door</em> if your
                kitchen has 36&quot;+ of swing clearance and you cook real meals. Pick{" "}
                <em className="italic text-[#ff9951] font-bold">side-by-side</em> if your kitchen
                is narrow, your budget is tight, or you buy tall bottles.
              </p>
            </div>
          </div>
        </div>
      }
      faqs={[
        {
          question: 'Does a French door really need 36" of clearance?',
          answer:
            'Each door is ~18" wide so together they swing 36". You can get away with 34" if nothing\'s directly opposite, but 36" lets both doors fully open without hitting a wall.',
        },
        {
          question: "Are French doors more energy-efficient?",
          answer:
            "Marginally. Opening one door instead of the whole fridge reduces cold-air loss. Over a year, expect ~$8–12 in savings vs. a comparable side-by-side.",
        },
        {
          question: "Which style breaks down more often?",
          answer:
            "Published 2024 reliability-survey data puts side-by-side slightly ahead for 10-year survival — mostly because fewer have through-door ice/water dispensers, the #1 failure point on both styles.",
        },
        {
          question: "Can I fit a pizza box in a side-by-side?",
          answer:
            'A standard 16" pizza box won\'t fit on most side-by-side shelves (14"–15" wide). Get a French door or a bottom-freezer if this matters.',
        },
      ]}
      relatedTools={[
        { title: "Find Your Fridge", category: "Quiz", description: "Explore refrigerator groups by your preferences.", href: "/tools/find-your-fridge", time: "~90 sec" },
        { title: "Counter Depth Fit", category: "Size & Capacity", description: "Door swing and clearance check.", href: "/tools/under-counter-refrigerator-fit-calculator", time: "~2 min" },
        { title: "Energy Cost Calculator", category: "Energy & Cost", description: "Compare annual cost for both styles.", href: "/tools/energy-cost-calculator", time: "~30 sec" },
        { title: "Refrigerator Size Calculator", category: "Size & Capacity", description: "Right cu ft for your household.", href: "/tools/refrigerator-size-calculator", time: "~45 sec" },
      ]}
      cta={{
        title: "Still torn? Take the 90-second quiz.",
        body: "Answer five questions to explore a starting product group, with prices where available. Verify dimensions, features and warranty terms on each product page.",
        buttonLabel: "Take the quiz",
        buttonHref: "/tools/find-your-fridge",
      }}
    >
      {/* Short answer is now rendered in the hero right-slot above — spec
          fight starts immediately so the head-to-head lands higher up. */}

      {/* ============ SPEC FIGHT ============ */}
      <section className="bg-white pt-8 md:pt-12">
        <div className="max-w-[880px] mx-auto px-6 md:px-8">
          <GuideTool
            eyebrow="Interactive match"
            title="Which style fits your kitchen?"
            intro="Three questions \u2014 aisle space, what you store, how you use the freezer \u2014 and the verdict names its reasons."
            questions={[
                          {
                            id: "aisle",
                            label: "Clearance in front of the fridge?",
                            options: [
                              { value: "narrow", label: "Under 36\u2033" },
                              { value: "mid", label: "36\u201342\u2033" },
                              { value: "wide", label: "42\u2033+" }
                            ],
                          },
                          {
                            id: "platters",
                            label: "Do wide platters and sheet trays go in?",
                            options: [
                              { value: "often", label: "Often" },
                              { value: "rarely", label: "Rarely" }
                            ],
                          },
                          {
                            id: "freezer",
                            label: "How do you eat?",
                            options: [
                              { value: "frozen", label: "Frozen-forward \u2014 daily freezer trips" },
                              { value: "fresh", label: "Fresh-forward \u2014 freezer is backup" }
                            ],
                          }
            ]}
            compute={(a) => {
                          if (a.aisle === "narrow" && a.platters !== "often") {
                            return { tone: "good", verdict: "Side-by-side", detail: "Under 36 inches of aisle, side-by-side's narrow doors are the practical win \u2014 each door needs roughly half the swing of a French door. " + (a.freezer === "frozen" ? "Daily freezer users also get the whole frozen column at eye level instead of a bend-down drawer." : "You give up wide-shelf storage, which you said you rarely need."), action: { label: "Browse side-by-side refrigerators", href: "/side-by-side-refrigerators" } }
                          }
                          if (a.platters === "often" || a.freezer === "fresh") {
                            return { tone: "good", verdict: "French door", detail: (a.platters === "often" ? "Sheet pans and platters are the French-door case in one line: full-width shelves with no center mullion. " : "") + (a.freezer === "fresh" ? "Fresh-forward households live at fridge level \u2014 French door puts all of it at eye height and saves the bend for the occasional freezer trip." : "Mind the door swing in tighter aisles \u2014 measure for the doors open at 90\u00b0."), action: { label: "Browse French door refrigerators", href: "/french-door-refrigerators" } }
                          }
                          return { tone: "good", verdict: "French door \u2014 by a nose", detail: "With workable aisle space and a frozen-forward pattern, both styles fit; French door wins the tiebreak on wide-shelf flexibility and resale-friendly popularity, while side-by-side remains the pick if the aisle ever tightens.", action: { label: "Compare both side by side", href: "/refrigerator-comparisons" } }
                        }}
            ctaLabel="Get my match"
            footnote="Clearance bands and trade-offs mirror the dimension section below."
          />
        </div>
      </section>

      <section className="bg-white py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <SectionRule
            eyebrow="Dimension by dimension"
            title="The spec fight"
            right="5 dimensions"
          />

          <div className="grid md:grid-cols-2 gap-5">
            {[SIDE_A, SIDE_B].map((s, i) => (
              <div
                key={s.name}
                className="rounded-xl p-6 md:p-7"
                style={{
                  border: `2px solid ${s.color}`,
                  background: i === 0 ? "rgba(4,105,240,0.03)" : "rgba(255,153,81,0.03)",
                }}
              >
                <div
                  className="flex items-center justify-between mb-4 pb-4 border-b"
                  style={{ borderColor: `${s.color}30` }}
                >
                  <div>
                    <div
                      className="text-[11px] font-bold uppercase tracking-[0.15em]"
                      style={{ color: s.color }}
                    >
                      {i === 0 ? "Side A" : "Side B"}
                    </div>
                    <div className="text-[26px] font-black tracking-tight text-gray-900">
                      {s.name}
                    </div>
                  </div>
                  <div className="text-4xl font-mono" style={{ color: s.color }}>
                    {s.icon}
                  </div>
                </div>

                <div className="space-y-3">
                  {Object.entries(s.score).map(([k, v]) => (
                    <div key={k}>
                      <div className="flex justify-between text-[12px] mb-1">
                        <span className="capitalize font-semibold">
                          {k === "narrow" ? "Narrow-kitchen fit" : k}
                        </span>
                        <span className="font-mono font-bold">{v}/100</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{ width: `${v}%`, background: s.color }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 pt-5 border-t"
                  style={{ borderColor: `${s.color}20` }}
                >
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-green-700 mb-2">
                      ✓ Pros
                    </div>
                    <ul className="space-y-1.5 text-[13px] text-gray-700">
                      {s.pros.map((p) => (
                        <li key={p} className="leading-[1.4]">
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-500 mb-2">
                      ✕ Cons
                    </div>
                    <ul className="space-y-1.5 text-[13px] text-gray-500">
                      {s.cons.map((c) => (
                        <li key={c} className="leading-[1.4]">
                          {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SCENARIOS ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[960px] mx-auto px-6 md:px-8">
          <SectionRule
            eyebrow="Which wins for you?"
            title="Six scenarios, six verdicts"
          />
          <div className="border-t border-gray-900">
            {SCENARIOS.map((s, i) => (
              <div
                key={s.who}
                className="grid grid-cols-[auto_1fr] md:grid-cols-[auto_1fr_auto] gap-3 md:gap-5 items-start md:items-center py-4 md:py-5 border-b border-gray-900"
              >
                <div className="font-mono text-[13px] font-bold min-w-[28px] text-[#ff9951] pt-1 md:pt-0">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div>
                  <h3 className="text-[15px] md:text-[16px] font-extrabold tracking-[-0.015em] text-gray-900">
                    {s.who}
                  </h3>
                  <p className="text-[13px] text-gray-600 mt-0.5 leading-[1.5]">{s.reason}</p>
                </div>
                <div className="col-start-2 md:col-auto md:text-right mt-2 md:mt-0">
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold text-white text-[12px]"
                    style={{ background: s.winner === "french" ? "#0469f0" : "#ff9951" }}
                  >
                    {s.winner === "french" ? "French door" : "Side-by-side"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL — alternative style to consider ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Top-freezer fridges if budget is tight"
          description="The most affordable style to buy and to run."
          collectionType="top-freezer"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ MODEL TABS (real DB carousel per side) ============ */}
      <section className="bg-white py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="Deep dive" title="Best picks on each side" />
          <div className="flex gap-2 mb-6 flex-wrap">
            {(
              [
                ["french", "French door"],
                ["side", "Side-by-side"],
              ] as const
            ).map(([v, l]) => (
              <button
                key={v}
                type="button"
                onClick={() => setTab(v)}
                className={[
                  "px-5 py-2.5 rounded-full font-bold text-[12px] transition-all border border-gray-900",
                  tab === v ? "bg-gray-900 text-white" : "bg-white text-gray-900 hover:bg-gray-50",
                ].join(" ")}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <ToolProductCarousel
          key={tab}
          title={tab === "french" ? "Top French door picks" : "Top Side-by-side picks"}
          collectionType={tab === "french" ? "french-door" : "side-by-side"}
          description={
            tab === "french"
              ? "Widest shelves, bottom-freezer access, and the highest resale value."
              : "Narrow-kitchen fit, eye-level freezer, and budget-friendly upfront price."
          }
        />
      </section>
    </ToolPageShell>
  )
}
